import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bool, func, object, string, number, array } from 'prop-types';
import _ from 'lodash';
import { metric } from '@fiverr-private/perseus';
import { getUgcData } from '@fiverr-private/gig_listings/src/GigCardListings/externalUtils/getUgcData';
import { getContext } from '@fiverr-private/fiverr_context';
import { buildSourceInfo } from '../../../utils/ugc/buildSourceInfo';
import { getCurrentContent } from '../../../utils/ugc/getCurrentContent';
import { getOriginalContent } from '../../../utils/ugc/getOriginalContent';
import { isTranslated } from '../../../utils/ugc/isTranslated';
import { updateListingsUGC, fetchUGC } from '../../../actions/ugc';
import { updateCollections } from '../../../actions/collections';
import { getGigs } from '../../../utils/gigCardCarouselService';
import { PAGE_NAME } from '../../../utils/events';

const GigCardCarouselHOCWrapper = (WrappedComponent) => {

    const GigCardCarouselHOC = (props, context) => {
        const {
            shouldFetchCarousel,
            type,
            url,
            onEmptyGigs,
            collections,
            updateCollections,
            carouselPosition,
            carouselContext,
            source,
            isTouch,
            showTranslatedUGC,
            originalListingsUGC,
            currentListingsUGC,
            updateListingsUGC,
            fetchUGC
        } = props;

        const {
            general,
            currencyConverter
        } = context;

        const { locale, userId } = getContext();
        const { gigId, subCategorySlug } = general;

        const [gigs, setGigs] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [gigsTranslated, setGigsTranslated] = useState(false);

        useEffect(() => {
            if (shouldFetchCarousel && isLoading && !gigs.length) {
                getGigsWrapper();
            }
        }, [shouldFetchCarousel, isLoading, gigs]);

        /* eslint react-hooks/exhaustive-deps: 0 */
        useEffect(() => {
            if (!originalListingsUGC && gigs.length) {
                updateListingsUGC(getUgcData(gigs, locale), type);
            }
        }, [originalListingsUGC, gigs]);

        useEffect(() => {
            if (showTranslatedUGC) {
                if (!currentListingsUGC) {
                    const sourceInfo = buildSourceInfo({ componentName: source, gigId });
                    fetchUGC({ sourceInfo, shouldToggle: false });
                } else {
                    enrichGigsWithListingUGC();
                }
            }
        }, [showTranslatedUGC, currentListingsUGC, gigs]);

        const getGigsWrapper = async() => {
            const getStartTime = Date.now();
            try {
                const gigs = await getGigs({ url, type });
                reportFetchGigsData(false, Date.now() - getStartTime);
                setGigs(gigs);
                setIsLoading(false);
            } catch (ex) {
                setIsLoading(false);
                onEmptyGigs();
                reportFetchGigsData(true, Date.now() - getStartTime);
            }
        };

        const reportFetchGigsData = (emptyGigs, responseTime) => {
            metric.count(`fetch_gigs_data.carousel.${type}.empty_gigs.${emptyGigs}`);
            metric.time(`fetch_gigs_data.carousel.${type}.time`, responseTime);
        };

        const enrichGigsWithListingUGC = () => {
            /*
            * return if gigsTranslated is true is  needed here or else the component
            * will get into an infinite loop since we're changing the state during useEffect
            * */

            if (gigsTranslated) {
                return;
            }

            const enrichedGigs = gigs.map((gig) => ({
                ...gig,
                ...currentListingsUGC[gig.gig_id] || {}
            }));

            setGigsTranslated(true);
            setGigs(enrichedGigs);
        };

        const getContextEnrichment = () => {
            const { pageCtxId, queryParameters } = getContext();
            const { ref } = queryParameters;

            return ({
                collectProps: {
                    collections,
                    onUpdateCollections: updateCollections
                },
                impressionEnrichment: {
                    listings: {
                        carousel_position: carouselPosition,
                        page_ctx_id: pageCtxId
                    }
                },
                context: {
                    queryParameters: {
                        context: carouselContext,
                        ref
                    },
                    sourcePage: PAGE_NAME,
                    sourceComponent: source,
                    currency: currencyConverter.getCurrency(),
                    pageCtxId,
                    userId,
                    subCategory: subCategorySlug,
                    viewportType: 'gigs.show',
                    currentPage: 'Gig page',
                    controllerAction: 'gigs#show'
                },
                isTouch
            });
        };

        if (!gigs.length && !isLoading) {
            return null;
        }

        return (
            <WrappedComponent {...props}
                gigs={gigs}
                isLoading={isLoading}
                contextEnrichment={getContextEnrichment()}/>
        );
    };

    GigCardCarouselHOC.propTypes = {
        showTranslatedUGC: bool,
        updateListingsUGC: func,
        type: string.isRequired,
        url: string.isRequired,
        onEmptyGigs: func,
        carouselPosition: number,
        collections: array,
        isTouch: bool,
        updateCollections: func,
        source: string,
        carouselContext: string,
        shouldFetchCarousel: bool
    };

    GigCardCarouselHOC.defaultProps = {
        showTranslatedUGC: false,
        updateListingsUGC: _.noop,
        onEmptyGigs: _.noop,
        collections: [],
        isTouch: false,
        updateCollections: _.noop,
        shouldFetchCarousel: true
    };

    GigCardCarouselHOC.contextTypes = {
        general: object,
        currencyConverter: object
    };

    const mapStateToProps = ({ ugc, collections }, { type }) => {
        const { listings: originalListingsUGC } = getOriginalContent({ ugc });
        const { listings: currentListingsUGC } = getCurrentContent({ ugc });
        const showTranslatedUGC = isTranslated(ugc);

        return {
            originalListingsUGC: originalListingsUGC[type],
            currentListingsUGC: currentListingsUGC[type],
            showTranslatedUGC,
            collections
        };
    };

    return connect(mapStateToProps, {
        updateListingsUGC,
        fetchUGC,
        updateCollections
    })(GigCardCarouselHOC);
};

export default GigCardCarouselHOCWrapper;
